import type { Phase } from '@val/shared'
import { useMemo, useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { ProgressBar, StatTile } from '../components/Bits.tsx'
import { CountingProgressNotice } from '../components/CountingProgressNotice.tsx'
import { CountTimeline } from '../components/CountTimeline.tsx'
import { Hemicycle } from '../components/Hemicycle.tsx'
import { PartyBars } from '../components/PartyBars.tsx'
import { HighlightPicker, KioskButton } from '../components/ValvakaControls.tsx'
import { useAleResult } from '../hooks/useAleResult.ts'
import { getHighlightParty } from '../lib/config.ts'
import { clockTime, int, pct } from '../lib/format.ts'
import './livepage.css'

const PHASE_LABELS: Record<Phase, string> = {
  preliminar: 'Preliminärt',
  slutlig: 'Slutligt',
  genrep: 'Generalrepetition',
}

export function LivePage() {
  const { kiosk } = useOutletContext<{ kiosk: boolean }>()
  const [params] = useSearchParams()
  // Fasen styrs från admin-sidan. ?phase= i URL:en finns kvar som override för test/kiosk.
  const phaseOverride = (params.get('phase') as Phase | null) ?? undefined
  const [highlight, setHighlight] = useState(getHighlightParty)

  const { result, timeline, loading, error, lastUpdated } = useAleResult(phaseOverride, true)

  const seatsByParty = useMemo(() => {
    const out: Record<string, number> = {}
    for (const p of result?.parties ?? []) out[p.code] = p.seatsComputed
    return out
  }, [result])

  if (loading && !result) {
    return <div className="livepage__loading">Hämtar valresultat …</div>
  }
  if (error && !result) {
    const notYet = /publicerat|inte tillgäng/i.test(error)
    if (notYet) {
      return (
        <CountingProgressNotice
          phase={phaseOverride}
          message={
            <>
              {error} Byt datamängd på <strong>Admin</strong> om du vill se generalrepetitionen så
              länge.
            </>
          }
        />
      )
    }
    return <div className="notice notice--error">Kunde inte hämta resultat: {error}</div>
  }
  if (!result) return null

  const highlightSeats = seatsByParty[highlight] ?? 0
  const highlight2022 = result.parties.find((p) => p.code === highlight)?.seats2022 ?? null
  const updatedLabel = lastUpdated
    ? clockTime(new Date(lastUpdated).toISOString())
    : result.updatedAt
      ? clockTime(result.updatedAt)
      : null

  return (
    <div className={`livepage${kiosk ? ' livepage--kiosk' : ''}`}>
      <div className="livepage__head">
        <div className="livepage__title">
          <h1>{kiosk ? 'Ale kommunfullmäktige' : 'Valvaka'}</h1>
          <span className={`badge ${result.final ? 'badge--final' : 'badge--live'}`}>
            {result.final ? 'Slutresultat' : `${PHASE_LABELS[result.phase]} · räknas`}
          </span>
        </div>
        <div className="livepage__meta">
          {updatedLabel && <span className="muted">Uppdaterad {updatedLabel}</span>}
          {!kiosk && (
            <>
              <HighlightPicker value={highlight} onChange={setHighlight} />
              <KioskButton />
            </>
          )}
        </div>
      </div>

      {result.mismatch && (
        <div className="notice">
          Vår mandatberäkning skiljer sig från Valmyndighetens fördelning i den här filen –
          dubbelkolla innan siffrorna används.
        </div>
      )}

      <div className="livepage__grid">
        <section className="card card__pad livepage__chart">
          <Hemicycle
            seatsByParty={seatsByParty}
            totalSeats={result.totalSeats}
            majority={result.majority}
            highlightParty={highlight}
            large={kiosk}
          />
          <p className="hemi-caption">
            <span className="hemi-caption__dot" />
            {highlight}: <strong>{highlightSeats}</strong> mandat
            {highlight2022 != null && (
              <span className="dim">
                {' '}
                ({highlightSeats > highlight2022 ? '+' : highlightSeats < highlight2022 ? '−' : '±'}
                {Math.abs(highlightSeats - highlight2022)} mot 2022)
              </span>
            )}
          </p>
        </section>

        <aside className="card card__pad livepage__count">
          <div className="card__title">Uppräkning</div>
          <div className="livepage__count-big tnum">
            {Math.round(result.countedRatio * 100)}
            <span className="livepage__count-unit"> % räknat</span>
          </div>
          <ProgressBar
            ratio={result.countedRatio}
            label={`${result.countedAreas} av ${result.totalAreas} distrikt`}
          />
          <div className="livepage__tiles">
            <StatTile
              label="Valdeltagande"
              value={result.turnoutPct != null ? pct(result.turnoutPct) : '–'}
            />
            <StatTile label="Giltiga röster" value={int(result.validVotes)} />
          </div>

          {(result.recentDistricts ?? []).length > 0 && (
            <div className="livepage__recent">
              <div className="livepage__recent-title">Senast rapporterade</div>
              <ul className="livepage__recent-list">
                {(result.recentDistricts ?? []).map((d) => (
                  <li key={d.name}>
                    <span className="livepage__recent-name">{d.name}</span>
                    <span className="dim tnum">{clockTime(d.reportedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <section className="card card__pad">
        <div className="card__title">Partier</div>
        <PartyBars parties={result.parties} highlightParty={highlight} />
      </section>

      {timeline && timeline.points.length > 1 && (
        <section className="card card__pad">
          <div className="card__title">Så växte resultatet fram</div>
          <CountTimeline timeline={timeline} highlightParty={highlight} />
        </section>
      )}
    </div>
  )
}
